import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { PaletteMode } from '@mui/material';

// Components
import Layout from './components/Layout';
import JobList from './pages/JobList';
import NotFound from './pages/NotFound';
import Loading from './components/Loading';
import UserRegistrationPopup, { UserRegistrationData } from './components/UserRegistrationPopup';
import StoreRegistrationDialog from './components/StoreRegistrationDialog';
import AccountReactivationDialog from './components/AccountReactivationDialog';
import PreserveStoreIdParam from './components/PreserveStoreIdParam';
import GlobalGetSkillCelebration from './components/GlobalGetSkillCelebration';
import ErrorBoundary from './components/ErrorBoundary';

// Context
import { AuthProvider, useAuth } from './auth/AuthContext';

// Services
import JobDetail from './pages/JobDetail';
import liffService from './services/liff';

// Mypage
import UserMypage from './pages/UserMypage';
import UserProfile from './pages/UserProfile';
import UserStoreManagement from './pages/UserStoreManagement';
import UserHelp from './pages/UserHelp';
import MyApplicationsPage from './pages/MyApplicationsPage';
import LaborConditionsPage from './pages/LaborConditionsPage';

// Attendance & Wallet
import AttendancePage from './pages/AttendancePage';
import WalletPage from './pages/WalletPage';
import ScoutPage from './pages/ScoutPage';
import BankAccountPage from './pages/BankAccountPage';

// Custom theme
const getTheme = (mode: PaletteMode) => 
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#5856D6', // メインカラーをアイコンの色味に合わせて変更
        dark: '#009624',
        light: '#5efc82',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#283044',
      },
      error: {
        main: '#e53935',
      },
      success: {
        main: '#43a047',
      },
      background: {
        default: '#f8f8f9',
        paper: '#ffffff',
      },
      text: {
        primary: '#212121',
        secondary: '#757575',
      }
    },
    typography: {
      fontFamily: [
        '"Noto Sans JP"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif'
      ].join(','),
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#5856D6', // ヘッダーをアイコンの色味に合わせて変更
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 24, // 丸みを帯びたボタン
          },
          containedPrimary: {
            backgroundColor: '#5856D6', // プライマリーボタンもアイコンの色味に合わせて変更
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16, // チップも丸みを帯びた
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)', // よりソフトな影
            borderRadius: 8, // カードの角も少し丸く
          },
        },
      },
    },
  });

// メインのアプリコンポーネント（AuthProviderの中で使用）
const AppContent: React.FC = () => {
  const [mode, setMode] = useState<PaletteMode>('light');
  // ?splash=3 のように秒数を指定するとスプラッシュ画面を強制表示（開発確認用）
  const [splashForced, setSplashForced] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('splash');
  });
  
  // ?splash クエリパラメータによる強制表示タイマー
  React.useEffect(() => {
    if (!splashForced) return;
    const params = new URLSearchParams(window.location.search);
    const delaySec = parseFloat(params.get('splash') || '3');
    const timer = setTimeout(() => setSplashForced(false), delaySec * 1000);
    return () => clearTimeout(timer);
  }, [splashForced]);

  // AuthContextから状態とメソッドを取得
  const {
    isAuthenticated,
    isLoading,
    needsRegistration,
    needsStoreRegistration,
    needsReactivation,
    pendingStoreName,
    error,
    registerUser,
    registerStore,
    cancelStoreRegistration,
    reactivateAccount,
    cancelReactivation,
    clearError
  } = useAuth();

  const theme = getTheme(mode);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // ユーザー登録完了時の処理
  // 成否をそのままポップアップへ返す。ここで握り潰すと失敗理由が誰にも伝わらない
  // （進捗表示と理由の提示はポップアップ側が担う）。
  const handleRegistrationComplete = async (userData: UserRegistrationData) => {
    return registerUser(userData);
  };

  // 登録ポップアップを閉じる
  const handleRegistrationClose = () => {
  };

  // 初期化中の表示（?splash クエリによる強制表示も含む）
  if (isLoading || splashForced) {
    return <Loading message="初期化中..." fullScreen />;
  }

  const redirectTarget = liffService.getQrToken() ? "/attendance" : "/jobs";

  // エラーが発生した場合の表示
  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '16px',
          backgroundColor: '#f5f5f5'
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            maxWidth: '400px',
            width: '100%'
          }}>
            <h1 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#e53935',
              marginBottom: '8px'
            }}>初期化エラー</h1>
            <p style={{ color: '#333' }}>{error}</p>
            <p style={{
              marginTop: '16px',
              color: '#666'
            }}>
              ページを更新するか、LINEアプリ内でアプリを開いてください。
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '16px',
                backgroundColor: '#5856D6',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '24px',
                border: 'none',
                width: '100%',
                cursor: 'pointer'
              }}
            >
              ページを更新
            </button>
            <button
              onClick={clearError}
              style={{
                marginTop: '8px',
                backgroundColor: 'transparent',
                color: '#5856D6',
                padding: '8px 16px',
                borderRadius: '24px',
                border: '1px solid #5856D6',
                width: '100%',
                cursor: 'pointer'
              }}
            >
              エラーをクリア
            </button>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <PreserveStoreIdParam />
        {/* GET SKILL お祝い演出: どの画面を開いても未読スキルがあれば表示（修正依頼 #48） */}
        <GlobalGetSkillCelebration />
        <Layout mode={mode} toggleColorMode={toggleColorMode}>
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Navigate to={redirectTarget} replace />} />
              <Route path="/jobs" element={<JobList />} />
              <Route path="/jobs/:id" element={<JobDetail />} />
              <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/bank-account" element={<BankAccountPage />} />
              <Route path="/scout" element={<ScoutPage />} />
              <Route path="/mypage" element={<UserMypage />} />
              <Route path="/my-applications" element={<MyApplicationsPage />} />
              <Route path="/labor-conditions" element={<LaborConditionsPage />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/store-Management/:storeUserId" element={<UserStoreManagement />} />
              <Route path="/help" element={<UserHelp />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
        </Layout>
      </Router>
      
      {/* ユーザー登録ポップアップ */}
      <UserRegistrationPopup
        open={needsRegistration && !isAuthenticated}
        onClose={handleRegistrationClose}
        onComplete={handleRegistrationComplete}
      />

      {/* 店舗登録確認ダイアログ（退会済みのときは再開ダイアログを優先するため出さない） */}
      <StoreRegistrationDialog
        open={needsStoreRegistration && !needsReactivation && !isAuthenticated}
        storeName={pendingStoreName || ''}
        onConfirm={registerStore}
        onCancel={cancelStoreRegistration}
      />

      {/* 退会済みアカウントの利用再開ダイアログ（080） */}
      <AccountReactivationDialog
        open={needsReactivation && !isAuthenticated}
        storeName={pendingStoreName || ''}
        onConfirm={reactivateAccount}
        onCancel={cancelReactivation}
      />
    </ThemeProvider>
  );
};

// メインのApp コンポーネント
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;